import React, { useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '../../assets/languages/configs';
import FujiModal from '../fuji-modal/FujiModal';
import useLocalizedMoney from '../../hooks/useLocalizedMoney';
import usePurchaseTicket from '../../hooks/usePurchaseTicket';
import { APPLE_MERCHANT_IDENTIFIER, SECRET_MANAGER_NAME } from '../../utils/Constants';
import { showNotificationDialog } from '../../utils';
import styles from './styles.module.css';
import './style.css';
import { OrderTicket } from 'types';

type ApplePayRefundConfirmModalProps = {
    orderTicket: OrderTicket;
    isConfirmModalOpen: boolean;
    setIsConfirmModalOpen: (open: boolean) => void;
    refundTicketWithApplePay: (
        orderTicket: OrderTicket,
        params: {
            orderId: string;
            cardToken?: string;
            paymentToken?: string;
            cancellationFee?: number;
        },
    ) => Promise<boolean | undefined>;
};

const ApplePayRefundConfirmModal: React.FC<ApplePayRefundConfirmModalProps> = ({
    orderTicket,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    refundTicketWithApplePay,
}) => {
    const { t } = useTranslation();
    const { formatMoney } = useLocalizedMoney();
    const { applePayPaymentSession } = usePurchaseTicket();
    const applePayButtonRef = useRef<HTMLElement | null>(null);

    const ApplePaySession = window.ApplePaySession;
    const cancellationFee = orderTicket?.cancellationFee ?? 0;

    const showErrorNotification = useCallback(
        (errorText: string) => {
            showNotificationDialog('error', '', t('notification.title'), errorText);
        },
        [t],
    );

    useEffect(() => {
        if (!navigator.onLine) {
            showErrorNotification(t('error-message.server-error'));
            return;
        }
        if (!ApplePaySession) {
            showErrorNotification(t('error-message.refund.apple-pay-refund-not-support'));
            setIsConfirmModalOpen(false);
            return;
        }
        const configData = JSON.parse(localStorage.getItem('APP_CONFIG') ?? '{}');
        const appName = configData?.appName ?? 'QUICK TRIP';
        const paymentRequest = {
            countryCode: 'JP',
            currencyCode: 'JPY',
            total: {
                label: appName,
                amount: String((orderTicket.refundFee ?? 0) + cancellationFee),
            },
            supportedNetworks: ['amex', 'visa', 'masterCard', 'jcb', 'discover'],
            merchantCapabilities: ['supports3DS'],
        };

        const handleApplePayClick = async () => {
            const applePaySession = new ApplePaySession(2, paymentRequest);
            setIsConfirmModalOpen(false);
            applePaySession.onvalidatemerchant = async (event: any) => {
                try {
                    const validationURL = event.validationURL;
                    // Apple Pay確認
                    const response = await applePayPaymentSession({
                        validationUrl: validationURL,
                        initiativeContext: window.location.origin,
                        displayName: 'QUICK TRIP',
                        merchantIdentifier: APPLE_MERCHANT_IDENTIFIER,
                        secretName: SECRET_MANAGER_NAME,
                    });
                    applePaySession.completeMerchantValidation(response);
                } catch {
                    applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
                }
            };
            applePaySession.onpaymentmethodselected = async () => {
                applePaySession.completePaymentMethodSelection({
                    newTotal: {
                        label: appName,
                        amount: String((orderTicket.refundFee ?? 0) + cancellationFee),
                    },
                });
            };
            applePaySession.onpaymentauthorized = async (event: any) => {
                const paymentToken = btoa(JSON.stringify(event.payment.token.paymentData));
                if (paymentToken) {
                    const isPaymentSuccess = await refundTicketWithApplePay(orderTicket, {
                        orderId: orderTicket.orderId,
                        paymentToken: paymentToken,
                        cancellationFee: orderTicket.cancellationFee ?? 0,
                    });
                    if (isPaymentSuccess) {
                        applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS);
                    } else {
                        applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
                    }
                } else {
                    applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
                }
            };
            applePaySession.oncancel = () => {
                console.log('cancel');
            };
            applePaySession.begin();
        };

        const applePayButton = applePayButtonRef.current;
        if (!applePayButton) return;

        const clickHandler = () => {
            if (typeof handleApplePayClick === 'function') {
                void handleApplePayClick();
            }
        };

        applePayButton.addEventListener('click', clickHandler);

        return () => {
            applePayButton.removeEventListener('click', clickHandler);
        };
    }, [
        applePayButtonRef,
        orderTicket,
        cancellationFee,
        ApplePaySession,
        t,
        refundTicketWithApplePay,
        setIsConfirmModalOpen,
        showErrorNotification,
        applePayPaymentSession,
    ]);

    const buttonLocale = (() => {
        const { language } = i18n;
        const localeDict: Record<string, string> = {
            ja: 'ja-JP',
            en: 'en-US',
            vi: 'vi-VN',
            ko: 'ko-KR',
            th: 'th-TH',
            zh: 'zh-CN',
            'zh-TW': 'zh-TW',
        };
        return localeDict?.[language] || 'en-US';
    })();
    return (
        <FujiModal
            isOpen={isConfirmModalOpen}
            onCancel={() => {
                setIsConfirmModalOpen(false);
            }}
            txtCancel={t('modal.refund-cancel') ?? ''}
            body={
                <div className="modal-content">
                    {t('modal.apple-pay-refund-confirmation')}
                    <div className={styles.refundInfo}>
                        {(orderTicket.refundFee ?? 0) > 0 && (
                            <div className={styles.refundFee}>
                                <span className={styles.label}>{t('ticket-detail.refundFee')}</span>
                                <span className={styles.divider}>:</span>
                                <div>{formatMoney(orderTicket.refundFee, 'JPY')}</div>
                            </div>
                        )}
                        {cancellationFee > 0 && (
                            <div className={styles.refundFee}>
                                <span className={styles.label}>{t('ticket-detail.cancellationFee')}</span>
                                <span className={styles.divider}>:</span>
                                <div>{formatMoney(cancellationFee, 'JPY')}</div>
                            </div>
                        )}
                        {orderTicket.refundFee != null && cancellationFee > 0 && (
                            <div className={styles.refundFee}>
                                <span className={styles.label}>{t('ticket-detail.total-amount')}</span>
                                <span className={styles.divider}>:</span>
                                <div>{formatMoney(orderTicket.refundFee + cancellationFee, 'JPY')}</div>
                            </div>
                        )}
                        {React.createElement('apple-pay-button', {
                            buttonstyle: 'black',
                            type: 'plain',
                            locale: buttonLocale,
                            ref: applePayButtonRef,
                        })}
                    </div>
                </div>
            }
        />
    );
};

export default ApplePayRefundConfirmModal;
